import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Badge, Box, Button, Card, Group, Text } from "@mantine/core";
import { Route, Routes, Navigate, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ErrorView, PageTitle, Subnav, Can } from "@components/shared";
import {
  RoleAdmins,
  RoleForm,
  RolePermissions,
} from "@components/Role";
import entityIcon from "@util/entityIcon";
import entityColor from "@util/entityColor";
import { PERMISSIONS, isOrgStaffCatalogRole } from "@util/permissions";

export default function RoleDetail() {
  const { t } = useTranslation();
  const [error, setError] = useState(null);
  const [role, setRole] = useState(null);
  const params = useParams();
  const navigate = useNavigate();
  const roleId = params.id;

  useEffect(() => {
    fetchData();
  }, [roleId]);

  function fetchData() {
    axios
      .get(`/roles/`)
      .then(({ data }) => {
        const match = (data.response || []).find(
          (r) => String(r.id) === String(roleId)
        );
        if (!match) {
          setError(t("not_found"));
          setRole(null);
          return;
        }
        setRole(match);
        setError(null);
      })
      .catch((err) => {
        setError(err);
        setRole(null);
      });
  }

  function deactivate() {
    axios
      .post(`/roles/${roleId}/deactivate/`, {})
      .then(() => {
        toast.success(t("updated"));
        navigate("/roles");
      })
      .catch((err) => toast.error(err));
  }

  function reactivate() {
    axios
      .post(`/roles/${roleId}/reactivate/`, {})
      .then(() => {
        toast.success(t("updated"));
        fetchData();
      })
      .catch((err) => toast.error(err));
  }

  if (error) {
    return <ErrorView text={error} />;
  }

  if (!role) {
    return <div>Loading...</div>;
  }

  const showPlatformAdminsTab = !isOrgStaffCatalogRole(role);
  const subnavLinks = [
    { text: t("detail"), to: `/roles/${roleId}` },
    {
      text: t("permission_plural"),
      to: `/roles/${roleId}/permissions`,
    },
  ];
  if (showPlatformAdminsTab) {
    subnavLinks.push({
      text: t("admin_plural"),
      to: `/roles/${roleId}/admins`,
    });
  }

  return (
    <div>
      <Group mb="lg">
        <PageTitle
          title={role.name}
          subtitle={t("role_singular")}
          icon={entityIcon.role()}
          iconColor={entityColor.role}
        />
        <Badge variant="light" color="gray">
          {(role.permissions || []).length} {t("permission_plural")}
        </Badge>
      </Group>
      <Box mt="md" mb="md">
        <Subnav links={subnavLinks} />
      </Box>
      <Routes>
        <Route
          path="/"
          element={
            <>
              <Card>
                <Can permission={PERMISSIONS.ROLES_EDIT} fallback={
                  <div>{role.name}</div>
                }>
                  <RoleForm
                    id={roleId}
                    name={role.name}
                    onSuccess={fetchData}
                  />
                </Can>
              </Card>
              <Can permission={PERMISSIONS.ROLES_DEACTIVATE}>
                <Card mt="sm">
                  <Text size="sm" c="dimmed" mb="sm">
                    {t("role_deactivate_description")}
                  </Text>
                  <Button color="red" variant="light" onClick={deactivate}>
                    {t("deactivate_role")}
                  </Button>
                </Card>
              </Can>
              <Can permission={PERMISSIONS.ROLES_REACTIVATE}>
                <Group mt="sm">
                  <Button variant="light" color="gray" onClick={reactivate}>
                    {t("reactivate")}
                  </Button>
                </Group>
              </Can>
            </>
          }
        />
        <Route
          path="/permissions"
          element={
            <RolePermissions
              roleId={roleId}
              permissions={role.permissions || []}
              onChange={fetchData}
            />
          }
        />
        <Route
          path="/admins"
          element={
            showPlatformAdminsTab ? (
              <RoleAdmins roleId={roleId} />
            ) : (
              <Navigate to={`/roles/${roleId}`} replace />
            )
          }
        />
      </Routes>
    </div>
  );
}
